/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: 해당 파일 사용되지 않는 enum 등 정리 필요

/**
 * SQLite 컬럼 타입 정의
 */
export type SQLiteColumnType = 'INTEGER' | 'TEXT' | 'REAL' | 'BLOB' | 'BOOLEAN' | 'TIMESTAMP'

/**
 * SQLite 컬럼 제약 조건
 */
export type SQLiteConstraint =
  | 'PRIMARY KEY'
  | 'AUTOINCREMENT'
  | 'NOT NULL'
  | 'UNIQUE'
  | 'DEFAULT CURRENT_TIMESTAMP'
  | `DEFAULT ${string}`

/**
 * SQLite 컬럼 정의
 */
export interface SQLiteColumn {
  type: SQLiteColumnType
  constraints?: SQLiteConstraint[]
}

/**
 * SQLite 외래 키 정의
 */
export interface SQLiteForeignKey {
  column: string
  reference: {
    table: string
    column: string
  }
  onDelete?: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'
  onUpdate?: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'
}

/**
 * SQL 관련 상수
 */
const SQL_TYPES = {
  INTEGER: 'INTEGER',
  TEXT: 'TEXT',
  REAL: 'REAL',
  BLOB: 'BLOB',
  BOOLEAN: 'BOOLEAN',
  TIMESTAMP: 'TIMESTAMP',
} as const

/**
 * TypeScript-SQLite 타입 매핑
 */
type TypeMapping = {
  number: typeof SQL_TYPES.INTEGER | typeof SQL_TYPES.REAL
  string: typeof SQL_TYPES.TEXT
  boolean: typeof SQL_TYPES.BOOLEAN
  Date: typeof SQL_TYPES.TIMESTAMP
  Uint8Array: typeof SQL_TYPES.BLOB
  null: 'NULL'
  undefined: 'NULL'
  any: SQLiteColumnType
}

type ValidateColumnType<
  TSType extends keyof TypeMapping,
  SQLType extends SQLiteColumnType,
> = SQLType extends TypeMapping[TSType] ? SQLType : never

/**
 * 테이블 스키마 타입 정의 (타입 검증 추가)
 * T는 테이블의 컬럼 구조를 정의하는 인터페이스
 */
export type TableSchema<T> = {
  [K in keyof T]: T[K] extends number
    ? { type: ValidateColumnType<'number', SQLiteColumnType>; constraints?: SQLiteConstraint[] }
    : T[K] extends string
      ? { type: ValidateColumnType<'string', SQLiteColumnType>; constraints?: SQLiteConstraint[] }
      : T[K] extends boolean
        ? {
            type: ValidateColumnType<'boolean', SQLiteColumnType>
            constraints?: SQLiteConstraint[]
          }
        : T[K] extends Date
          ? { type: ValidateColumnType<'Date', SQLiteColumnType>; constraints?: SQLiteConstraint[] }
          : T[K] extends Uint8Array
            ? {
                type: ValidateColumnType<'Uint8Array', SQLiteColumnType>
                constraints?: SQLiteConstraint[]
              }
            : SQLiteColumn
} & {
  __foreignKeys?: SQLiteForeignKey[]
}

/**
 * 테이블 정의 인터페이스
 * T는 테이블의 컬럼 구조를 정의하는 인터페이스
 */
export interface TableDefinition<T> {
  name: string
  schema: TableSchema<T>
}

/**
 * 테이블 스키마를 SQL 문자열로 변환
 * @param tableName 테이블 이름
 * @param schema 테이블 스키마
 * @returns SQL CREATE TABLE 문자열
 */
export function generateSchemaSQL<T>(tableName: string, schema: TableSchema<T>): string {
  const columns: string[] = []
  const foreignKeys: string[] = []

  for (const [columnName, column] of Object.entries(schema)) {
    if (columnName === '__foreignKeys') {
      continue
    }

    // SQLiteForeignKey[] 타입 체크 추가
    if (Array.isArray(column) && column.length > 0 && 'column' in column[0]) {
      continue // 외래 키 배열은 건너뜀
    }

    // unknown으로 먼저 변환하여 타입 오류 방지
    const columnDef = generateColumnDefinition(columnName, column as unknown as SQLiteColumn)
    columns.push(columnDef)
  }

  if (schema.__foreignKeys) {
    foreignKeys.push(...generateForeignKeyDefinitions(schema.__foreignKeys))
  }

  const allDefinitions = [...columns, ...foreignKeys]
  return generateCreateTableSQL(tableName, allDefinitions)
}

function generateColumnDefinition(columnName: string, column: SQLiteColumn): string {
  let type: SQLiteColumnType = column.type

  // SQL 예약어 목록
  const SQL_RESERVED_WORDS = [
    'ADD',
    'ALL',
    'ALTER',
    'AND',
    'AS',
    'ASC',
    'BETWEEN',
    'BY',
    'CASE',
    'CHECK',
    'COLUMN',
    'CONSTRAINT',
    'CREATE',
    'DEFAULT',
    'DELETE',
    'DESC',
    'DISTINCT',
    'DROP',
    'ELSE',
    'END',
    'ESCAPE',
    'EXCEPT',
    'EXISTS',
    'FOREIGN',
    'FROM',
    'GROUP',
    'HAVING',
    'IN',
    'INDEX',
    'INNER',
    'INSERT',
    'INTERSECT',
    'INTO',
    'IS',
    'JOIN',
    'KEY',
    'LEFT',
    'LIKE',
    'LIMIT',
    'NOT',
    'NULL',
    'ON',
    'OR',
    'ORDER',
    'OUTER',
    'PRIMARY',
    'REFERENCES',
    'RIGHT',
    'SELECT',
    'SET',
    'TABLE',
    'THEN',
    'TO',
    'TRANSACTION',
    'UNION',
    'UNIQUE',
    'UPDATE',
    'USING',
    'VALUES',
    'WHEN',
    'WHERE',
  ]

  // 예약어인 경우 백틱으로 감싸기
  const safeColumnName = SQL_RESERVED_WORDS.includes(columnName.toUpperCase())
    ? `\`${columnName}\``
    : columnName

  // column.type의 타입에따라 타입 변환
  if (column.type === undefined) {
    switch (typeof column) {
      case 'number':
        type = 'INTEGER'
        break
      case 'string':
        type = 'TEXT'
        break
      case 'boolean':
        type = 'BOOLEAN'
        break
      case 'object':
        if (column instanceof Date) {
          type = 'TIMESTAMP'
        } else if (column instanceof Uint8Array) {
          type = 'BLOB'
        } else {
          type = 'TEXT'
        }
        break
      default:
        type = 'TEXT'
        break
    }
  }

  // 안전한 컬럼명 사용
  let columnDef = `${safeColumnName} ${type}`

  if (column.constraints?.length) {
    columnDef += ` ${column.constraints.join(' ')}`
  }

  return columnDef
}

function generateForeignKeyDefinitions(foreignKeys: SQLiteForeignKey[]): string[] {
  return foreignKeys.map(fk => {
    let fkDef = `FOREIGN KEY (${fk.column}) REFERENCES ${fk.reference.table}(${fk.reference.column})`

    if (fk.onDelete) {
      fkDef += ` ON DELETE ${fk.onDelete}`
    }

    if (fk.onUpdate) {
      fkDef += ` ON UPDATE ${fk.onUpdate}`
    }

    return fkDef
  })
}

function generateCreateTableSQL(tableName: string, definitions: string[]): string {
  return `
    CREATE TABLE IF NOT EXISTS ${tableName} (
      ${definitions.join(',\n      ')}
    );
  `
}

/**
 * 데이터베이스 테이블 정의를 생성
 * @param name 테이블 이름
 * @param schema 테이블 스키마
 * @returns 테이블 정의 객체
 */
export function defineTable<T>(name: string, schema: TableSchema<T>): TableDefinition<T> {
  return {
    name,
    schema,
  }
}

/**
 * 데이터베이스 스키마 정의를 SQL로 변환
 * @param tables 테이블 정의 객체
 * @returns 테이블 이름과 SQL 스키마를 포함하는 객체
 */
export function createDatabaseSchema<T extends Record<string, TableDefinition<any>>>(
  tables: T,
): Record<keyof T, { name: string; schema: string }> {
  return Object.entries(tables).reduce(
    (result, [tableName, table]) => {
      return {
        ...result,
        [tableName]: {
          name: table.name,
          schema: generateSchemaSQL(table.name, table.schema),
        },
      }
    },
    {} as Record<keyof T, { name: string; schema: string }>,
  )
}

/**
 * JavaScript 값의 타입에 기반하여 SQLite 컬럼 타입을 추론합니다.
 * @param value 타입을 추론할 JavaScript 값
 * @returns SQLite 컬럼 타입
 */
export function inferSQLiteColumnType(value: any): SQLiteColumnType {
  // null 또는 undefined는 TEXT로 처리
  if (value === null || value === undefined) {
    return 'TEXT'
  }

  // 값 타입에 따라 SQLite 타입 반환
  switch (typeof value) {
    case 'number':
      return Number.isInteger(value) ? 'INTEGER' : 'REAL'
    case 'boolean':
      return 'BOOLEAN' // SQLite는 BOOLEAN 타입이 없지만, 타입 정의에 맞게 BOOLEAN 반환
    case 'string':
      return 'TEXT'
    case 'object':
      if (value instanceof Date) {
        return 'TIMESTAMP' // 날짜는 TIMESTAMP로 저장
      }
      if (value instanceof Uint8Array || value instanceof ArrayBuffer) {
        return 'BLOB'
      }
      return 'TEXT' // 객체는 JSON 문자열로 저장
    default:
      return 'TEXT'
  }
}

/**
 * JavaScript 객체의 값을 SQLite에 저장 가능한 형태로 변환합니다.
 * @param value 변환할 값
 * @returns SQLite에 저장 가능한 값
 */
export function prepareSQLiteValue(value: any): any {
  if (value === null || value === undefined) {
    return null
  }

  switch (typeof value) {
    case 'number':
      // iOS Capacitor SQLite native bridge가 NSNumber를 TEXT 컬럼에 저장할 때
      // 정수 number(예: 15522)를 "15522.0"으로 stringify하는 문제를 우회한다.
      // JS의 String()은 정수 number를 "15522"로, 0.5는 "0.5"로 정확히 변환하므로
      // JS 측에서 미리 string 변환해 native bridge가 손대지 못하게 한다.
      // SQLite는 numeric affinity로 INTEGER/REAL 컬럼에도 string을 그대로 받는다.
      return String(value)
    case 'string':
    case 'boolean':
      return value
    case 'object':
      if (value instanceof Date) {
        return Math.floor(value.getTime() / 1000) // Unix timestamp
      }
      return JSON.stringify(value) // 객체는 JSON 문자열로 변환
    default:
      return String(value) // 기타 타입은 문자열로 변환
  }
}

/**
 * 객체로부터 SQLite 테이블 스키마를 생성합니다.
 * @param obj 스키마 생성에 사용할 객체
 * @param options 스키마 생성 옵션 (기본 키 필드 등)
 * @returns 컬럼 정의가 포함된 테이블 스키마 객체
 */
export function generateObjectTableSchema(
  obj: any,
  options: {
    idField?: string
    idType?: SQLiteColumnType
    additionalFields?: Record<string, SQLiteColumn>
  } = {},
): TableSchema<any> {
  const { idField = 'id', idType = 'TEXT', additionalFields = {} } = options

  const schema: Record<string, SQLiteColumn> = {
    [idField]: { type: idType, constraints: ['PRIMARY KEY'] },
  }

  // 추가 필드 병합
  Object.entries(additionalFields).forEach(([key, column]) => {
    schema[key] = column
  })

  if (typeof obj !== 'object' || obj === null) {
    return schema as TableSchema<any>
  }

  // 객체의 각 필드를 분석하여 스키마에 추가
  for (const [key, value] of Object.entries(obj)) {
    if (key === idField) continue // ID 필드는 이미 처리했으므로 스킵

    if (!schema[key]) {
      schema[key] = {
        type: inferSQLiteColumnType(value),
      }
    }
  }

  return schema as TableSchema<any>
}

/**
 * JavaScript 객체에서 테이블 스키마를 생성하고 SQL 문을 반환합니다.
 * @param tableName 테이블 이름
 * @param obj 스키마 기반이 될 객체
 * @param options 스키마 생성 옵션
 * @returns CREATE TABLE SQL 문
 */
export function generateTableSQLFromObject(
  tableName: string,
  obj: any,
  options: {
    idField?: string
    idType?: SQLiteColumnType
    additionalFields?: Record<string, SQLiteColumn>
    includeDeletedField?: boolean
  } = {},
): string {
  const { includeDeletedField = true } = options

  // 삭제 필드 포함 옵션이 활성화된 경우 추가 필드에 포함
  if (includeDeletedField) {
    if (!options.additionalFields) {
      options.additionalFields = {}
    }

    if (!options.additionalFields.deleted) {
      options.additionalFields.deleted = {
        type: 'INTEGER',
        constraints: ['DEFAULT 0'],
      }
    }
  }

  const schema = generateObjectTableSchema(obj, options)
  return generateSchemaSQL(tableName, schema)
}

/**
 * 객체 배열에서 첫 번째 객체를 사용하여 테이블 스키마 SQL을 생성합니다.
 * @param tableName 테이블 이름
 * @param objectArray 객체 배열 (첫 번째 객체만 사용)
 * @param options 스키마 생성 옵션
 * @returns CREATE TABLE SQL 문
 */
export function generateTableSQLFromObjectArray(
  tableName: string,
  objectArray: Record<string, any>[],
  options: {
    idField?: string
    idType?: SQLiteColumnType
    additionalFields?: Record<string, SQLiteColumn>
    includeDeletedField?: boolean
  } = {},
): string {
  if (!Array.isArray(objectArray) || objectArray.length === 0) {
    throw new Error('최소 하나 이상의 객체를 포함하는 배열을 제공해야 합니다.')
  }

  return generateTableSQLFromObject(tableName, objectArray[0], options)
}

export type SQLiteAffinity = 'INTEGER' | 'TEXT' | 'BLOB' | 'REAL' | 'NUMERIC'

/**
 * 선언 타입 → 컬럼 affinity (SQLite datatype3 3.1절 규칙 그대로)
 * BOOLEAN·TIMESTAMP 는 NUMERIC 이다.
 * @param declaredType PRAGMA table_info 의 type
 */
export function getColumnAffinity(declaredType: string | null | undefined): SQLiteAffinity {
  const t = (declaredType ?? '').toUpperCase()
  if (t.includes('INT')) return 'INTEGER'
  if (t.includes('CHAR') || t.includes('CLOB') || t.includes('TEXT')) return 'TEXT'
  if (t === '' || t.includes('BLOB')) return 'BLOB'
  if (t.includes('REAL') || t.includes('FLOA') || t.includes('DOUB')) return 'REAL'
  return 'NUMERIC'
}

// SQLite 가 숫자로 바꿔 저장하는 텍스트(공백 없는 10진 리터럴)
const NUMERIC_TEXT_RE = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/

/** 저장 후 SELECT 결과를 확신할 수 없는 값 */
export const UNSURE_SQLITE_VALUE = Symbol('unsureSQLiteValue')

/**
 * prepareSQLiteValue 결과를 해당 컬럼에 썼을 때 SELECT 로 돌려받을 값을 계산한다.
 * 확신할 수 없는 경우(텍스트 컬럼의 boolean, 공백 섞인 숫자 텍스트, 안전 범위 밖 정수 등)는
 * UNSURE_SQLITE_VALUE 를 돌려준다 — 호출자는 그때 SELECT 로 다시 읽어야 한다.
 * @param value prepareSQLiteValue 결과
 * @param affinity 컬럼 affinity
 */
export function toStoredSQLiteValue(value: unknown, affinity: SQLiteAffinity): unknown {
  if (value === null || value === undefined) return null
  const numeric = affinity === 'INTEGER' || affinity === 'NUMERIC' || affinity === 'REAL'
  if (typeof value === 'boolean') return numeric ? (value ? 1 : 0) : UNSURE_SQLITE_VALUE
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value)) return UNSURE_SQLITE_VALUE
    if (affinity === 'TEXT') return String(value)
    return value
  }
  if (typeof value !== 'string') return UNSURE_SQLITE_VALUE
  if (!numeric) return value
  if (NUMERIC_TEXT_RE.test(value)) {
    // 유효숫자 15자리 초과는 SQLite 와 JS 의 실수 변환 반올림이 다를 수 있다
    if (
      value
        .replace(/[eE].*$/, '')
        .replace(/\D/g, '')
        .replace(/^0+/, '').length > 15
    ) {
      return UNSURE_SQLITE_VALUE
    }
    const n = Number(value)
    if (!Number.isFinite(n)) return UNSURE_SQLITE_VALUE
    // 정수로 저장되는 값이 안전 범위를 넘으면 JS number 로 같은 값을 만들 수 없다
    if (Number.isInteger(n) && !Number.isSafeInteger(n)) return UNSURE_SQLITE_VALUE
    return n
  }
  if (value.trim() !== value && NUMERIC_TEXT_RE.test(value.trim())) return UNSURE_SQLITE_VALUE
  return value
}
